import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useToast } from '../components/ToastContext';
import { useI18n } from '../i18n';
import html2pdf from 'html2pdf.js';
import { BadgeCheckIcon, CheckCircleIcon, CircleAlertIcon, EyeIcon, XCircleIcon } from 'lucide-react';
import './Steps.css';
import './Steps_TAB.css';

/**
 * Step 3: AI Validation & Report Display
 * Two Tabs:
 * 1. Report - Validation results with table/grid view
 * 2. Segments - Document segments with thumbnails
 */

// Translation mapping for common document terms
const translationMap = {
  // Identity & Personal Documents
  'saudi national identity document': 'بطاقة الهوية الوطنية السعودية',
  'national identity': 'الهوية الوطنية',
  'civil id': 'بطاقة الأحوال المدنية',
  'driving license': 'رخصة القيادة',
  'passport': 'جواز السفر',
  
  // Property Documents
  'real estate ownership document': 'وثيقة تملك عقار',
  'property deed': 'صك الملكية',
  'ownership deed': 'صك التملك',
  'deed': 'الصك',
  
  // Commercial Documents
  'commercial registration certificate': 'شهادة السجل التجاري',
  'commercial registration': 'السجل التجاري',
  'tax certificate': 'الشهادة الضريبية',
  'vat certificate': 'شهادة ضريبة القيمة المضافة',
  
  // Building & Construction
  'building permit': 'رخصة البناء',
  'construction permit': 'تصريح البناء',
  'building license': 'ترخيص البناء',
  'thermal insulation completion certificate': 'شهادة إتمام العزل الحراري',
  'insulation certificate': 'شهادة العزل',
  'engineering site layout plan': 'مخطط الموقع الهندسي',
  'architectural plans': 'المخططات المعمارية',
  'floor plan': 'مخطط الطابق',
  'elevation plan': 'مخطط الواجهة',
  
  // Technical & Reports
  'technical report': 'التقرير الفني',
  'engineering report': 'التقرير الهندسي',
  'inspection report': 'تقرير المعاينة',
  'survey report': 'تقرير المسح',
  
  // Insurance
  'insurance policy': 'وثيقة التأمين',
  'insurance certificate': 'شهادة التأمين',
  'liability insurance': 'تأمين المسؤولية',
  'defect insurance': 'تأمين العيوب الخفية',
  
  // Photos & Images
  'building photos': 'صور المبنى',
  'facade photos': 'صور الواجهة',
  'site photos': 'صور الموقع',
  'construction photos': 'صور البناء',
  
  // Forms & Declarations
  'form': 'نموذج',
  'declaration': 'إقرار',
  'undertaking': 'تعهد',
  'commitment': 'التزام',
  
  // General
  'official document': 'مستند رسمي',
  'certificate': 'شهادة',
  'letter': 'خطاب',
  'contract': 'عقد',
  'agreement': 'اتفاقية',
  'invoice': 'فاتورة',
  'receipt': 'إيصال'
};

/**
 * Translate English document title to Arabic
 * @param {string} title - Document title (English or Arabic)
 * @returns {string} - Formatted as "English | Arabic" or original if already Arabic
 */
function translateSegmentTitle(title) {
  if (!title) return '';
  
  const lowerTitle = title.toLowerCase().trim();
  
  // Check if title is already in Arabic (contains Arabic characters)
  const hasArabic = /[\u0600-\u06FF]/.test(title);
  if (hasArabic) {
    return title; // Already Arabic, return as-is
  }
  
  // Try exact match first
  if (translationMap[lowerTitle]) {
    return `${title} | ${translationMap[lowerTitle]}`;
  }
  
  // Try partial match (find if any key is contained in the title)
  for (const [english, arabic] of Object.entries(translationMap)) {
    if (lowerTitle.includes(english)) {
      return `${title} | ${arabic}`;
    }
  }
  
  // No translation found, return original
  return title;
}

function Step3Report({ report: reportProp, datalabPromise, requestData, datalabTime: initialDatalabTime, aiTime: initialAiTime, onBack, onReset, urlRequestId }) {
  const { showToast } = useToast();
  const { t, lang } = useI18n();
  const reportRef = useRef(null);
  const hasProcessed = useRef(false);
  
  const [report, setReport] = useState(reportProp);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(t('step3.loadingText'));
  const [error, setError] = useState('');
  const [datalabTime, setDatalabTime] = useState(initialDatalabTime || 0);
  const [aiTime, setAiTime] = useState(initialAiTime || 0);
  const [activeTab, setActiveTab] = useState('report');
  const [markdownFiles, setMarkdownFiles] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState({ open: false, rule: null, pages: [] });
  const [imagePreview, setImagePreview] = useState({ open: false, src: '', alt: '' }); // Full-screen image preview
  
  // Inline document preview panel state
  const [activeRulePreview, setActiveRulePreview] = useState(null); // Currently previewed rule
  const [activePreviewIndex, setActivePreviewIndex] = useState(0); // Currently selected thumbnail index

  // Format time helper function
  const formatTime = (ms) => {
    if (!ms || ms === 0) return '—';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  // Calculate total time
  const totalTime = (datalabTime || 0) + (aiTime || 0);

  // Helper function to map decision to CSS class
  const getDecisionClass = (decision) => {
    switch (decision) {
      case 'passed':
      case 'not_applicable':
        return 'success';
      case 'failed':
      case 'needs_review':
        return 'failed';
      default:
        return 'failed';
    }
  };

  // Helper function to get decision icon
  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'passed':
        return '✓';
      case 'not_applicable':
        return '○';
      case 'needs_review':
        return '⚠';
      case 'failed':
        return '✗';
      default:
        return '✗';
    }
  };

  // Helper function to get decision label
  const getDecisionLabel = (decision) => {
    switch (decision) {
      case 'passed':
        return t('status.passed');
      case 'not_applicable':
        return t('status.notApplicable');
      case 'needs_review':
        return t('status.needsReview');
      case 'failed':
        return t('status.failed');
      default:
        return t('status.failed');
    }
  };

  useEffect(() => {
    // Process both APIs sequentially if not already processed
    if (hasProcessed.current) {
      return;
    }

    const processBothAPIs = async () => {
      hasProcessed.current = true;
      setLoading(true);
      setError('');

      try {
        let finalMarkdownFiles = null;
        let finalDatalabTime = initialDatalabTime || 0;

        // Step 1: Wait for Datalab API if promise exists
        if (datalabPromise) {
          setLoadingMessage(t('step3.loadingDatalab'));
          
          const datalabResult = await datalabPromise;
          
          if (datalabResult.error) {
            throw new Error(datalabResult.error);
          }

          finalMarkdownFiles = datalabResult.markdownFiles;
          finalDatalabTime = datalabResult.datalabTime || 0;
          setDatalabTime(finalDatalabTime);
          setMarkdownFiles(finalMarkdownFiles); // Store for chat
        }

        // Step 2: Call OpenRouter/OpenAI API
        if (!requestData) {
          throw new Error('لا توجد بيانات الطلب');
        }

        if (!finalMarkdownFiles || finalMarkdownFiles.length === 0) {
          throw new Error('لا توجد ملفات للتحقق منها');
        }

        setLoadingMessage(t('step3.loadingAI'));

        const aiStartTime = Date.now();
        const result = await api.validateDocuments(requestData, finalMarkdownFiles);
        const aiEndTime = Date.now();
        const finalAiTime = aiEndTime - aiStartTime;

        if (!result.success) {
          throw new Error('فشل التحقق من الاشتراطات');
        }

        setReport(result.report);
        setAiTime(finalAiTime);
        setLoading(false);
        showToast('اكتملت عملية التحقق بنجاح', 'success');

      } catch (err) {
        console.error('Processing error:', err);
        const errorMessage = err.response?.data?.error || err.message || 'حدث خطأ أثناء معالجة البيانات';
        setError(errorMessage);
        setLoading(false);
        showToast(errorMessage, 'error');
      }
    };

    // If report is already provided, skip processing
    if (reportProp) {
      setLoading(false);
      return;
    }

    // Otherwise, process both APIs
    processBothAPIs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datalabPromise, requestData, reportProp]);

  const handlePrintReport = () => {
    // Create a print-friendly HTML structure
    const passedCount = report.rulesIndex.filter(r => r.decision === 'passed' || r.decision === 'not_applicable').length;
    const failedCount = report.rulesIndex.filter(r => r.decision === 'failed' || r.decision === 'needs_review').length;
    
    const printContent = `
      <div dir="rtl" style="font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; color: #1a1a1a;">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px;">
          <h1 style="margin: 0; font-size: 24px; color: #1e3a5f;">تقرير التحقق من المستندات</h1>
          <p style="margin: 10px 0 0; color: #666;">تاريخ التقرير: ${new Date().toLocaleDateString('ar-SA')}</p>
        </div>

        <!-- Info Section -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 25px; background: #f8fafc; padding: 15px; border-radius: 8px;">
          <div><strong>رقم الهوية:</strong> ${report.civilId || '—'}</div>
          <div><strong>رقم الصك:</strong> ${report.requestId || '—'}</div>
        </div>

        <!-- Status Banner -->
        <div style="padding: 15px; border-radius: 8px; margin-bottom: 25px; background: ${report.finalStatus === 'success' ? '#ecfdf5' : report.finalStatus === 'partial' ? '#fffbeb' : '#fef2f2'}; border: 1px solid ${report.finalStatus === 'success' ? '#10b981' : report.finalStatus === 'partial' ? '#f59e0b' : '#ef4444'};">
          <h2 style="margin: 0 0 8px; font-size: 18px; color: ${report.finalStatus === 'success' ? '#065f46' : report.finalStatus === 'partial' ? '#92400e' : '#991b1b'};">
            ${report.finalStatus === 'success' ? '✓ اكتملت المراجعة بنجاح' : report.finalStatus === 'partial' ? '⚠ مراجعة جزئية' : '✗ فشلت المراجعة'}
          </h2>
          <p style="margin: 0; color: #666;">${report.summary?.ar || ''}</p>
        </div>

        <!-- Summary Stats -->
        <div style="display: flex; gap: 15px; margin-bottom: 25px;">
          <div style="flex: 1; text-align: center; padding: 15px; background: #ecfdf5; border-radius: 8px; border: 1px solid #10b981;">
            <div style="font-size: 28px; font-weight: bold; color: #065f46;">${passedCount}</div>
            <div style="color: #065f46;">اشتراطات مطابقة</div>
          </div>
          <div style="flex: 1; text-align: center; padding: 15px; background: #fef2f2; border-radius: 8px; border: 1px solid #ef4444;">
            <div style="font-size: 28px; font-weight: bold; color: #991b1b;">${failedCount}</div>
            <div style="color: #991b1b;">اشتراطات غير مطابقة</div>
          </div>
          <div style="flex: 1; text-align: center; padding: 15px; background: #f1f5f9; border-radius: 8px; border: 1px solid #94a3b8;">
            <div style="font-size: 28px; font-weight: bold; color: #475569;">${report.rulesIndex.length}</div>
            <div style="color: #475569;">إجمالي الاشتراطات</div>
          </div>
        </div>

        <!-- Rules Details -->
        <h3 style="font-size: 18px; margin-bottom: 15px; color: #1e3a5f; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">تفاصيل الاشتراطات</h3>
        ${report.rulesIndex.map((rule, index) => {
          const isPassed = rule.decision === 'passed' || rule.decision === 'not_applicable';
          const confidence = rule.confidence !== undefined ? Math.round(rule.confidence * 100) : null;
          
          return `
            <div style="margin-bottom: 20px; padding: 15px; border-radius: 8px; border: 1px solid ${isPassed ? '#10b981' : '#ef4444'}; background: ${isPassed ? '#f0fdf4' : '#fef2f2'};">
              <!-- Rule Header -->
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                <div style="display: flex; align-items: center; gap: 10px;">
                  <span style="background: ${isPassed ? '#dcfce7' : '#fee2e2'}; color: ${isPassed ? '#166534' : '#991b1b'}; padding: 4px 10px; border-radius: 50%; font-weight: bold;">${index + 1}</span>
                  <strong style="font-size: 15px;">${rule.title?.ar || rule.title}</strong>
                </div>
                <span style="padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; background: ${isPassed ? '#dcfce7' : '#fee2e2'}; color: ${isPassed ? '#166534' : '#991b1b'};">
                  ${isPassed ? '✓ ناجح' : '✗ مرفوض'}
                </span>
              </div>

              <!-- Rule Message -->
              <div style="background: #fff; padding: 10px; border-radius: 6px; margin-bottom: 10px;">
                <p style="margin: 0; color: #374151;">${rule.message?.ar || rule.message?.en || '—'}</p>
              </div>

              <!-- Matched Fields -->
              ${rule.matchedFields && rule.matchedFields.length > 0 ? `
                <div style="margin-top: 10px;">
                  <strong style="font-size: 13px; color: #4b5563;">تفاصيل المطابقة:</strong>
                  ${rule.matchedFields.map(field => `
                    <div style="margin-top: 8px; padding: 8px; background: ${field.match ? '#f0fdf4' : '#fef2f2'}; border-radius: 6px; border: 1px solid ${field.match ? '#bbf7d0' : '#fecaca'};">
                      <div style="font-weight: 600; color: #374151; margin-bottom: 5px;">${field.key}</div>
                      <div style="display: flex; gap: 20px; font-size: 12px;">
                        <span><strong>المتوقع:</strong> <code style="background: #fff; padding: 2px 6px; border-radius: 3px;">${field.expected || '—'}</code></span>
                        <span><strong>الموجود:</strong> <code style="background: #fff; padding: 2px 6px; border-radius: 3px; color: ${field.match ? '#166534' : '#dc2626'};">${field.found || '—'}</code></span>
                        <span style="color: ${field.match ? '#166534' : '#dc2626'}; font-weight: 600;">${field.match ? '✓ مطابق' : '✗ غير مطابق'}</span>
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}

              <!-- Confidence -->
              ${confidence !== null ? `
                <div style="margin-top: 10px; font-size: 12px; color: #6b7280;">
                  <strong>نسبة الثقة:</strong> ${confidence}%
                </div>
              ` : ''}
            </div>
          `;
        }).join('')}

        <!-- Footer -->
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px;">
          <p>تم إنشاء هذا التقرير آلياً بواسطة نظام التحقق من المستندات</p>
        </div>
      </div>
    `;

    // Create temporary element for PDF generation
    const printElement = document.createElement('div');
    printElement.innerHTML = printContent;
    document.body.appendChild(printElement);

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `تقرير-${report.civilId || 'unknown'}-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(printElement).save().then(() => {
      document.body.removeChild(printElement);
    });
  };

  const handlePreview = (rule) => {
    if (!rule.evidence?.pages || rule.evidence.pages.length === 0) {
      showToast('لا توجد أدلة مرئية لهذه القاعدة', 'info');
      return;
    }
    // Toggle preview if same rule is clicked
    if (activeRulePreview?.ruleId === rule.ruleId) {
      closeInlinePreview();
      return;
    }
    // Set inline preview panel state
    setActiveRulePreview(rule);
    setActivePreviewIndex(0); // Auto-select first thumbnail
  };

  const closePreview = () => {
    setPreviewModal({ open: false, rule: null, pages: [] });
  };

  // Close inline preview panel
  const closeInlinePreview = () => {
    setActiveRulePreview(null);
    setActivePreviewIndex(0);
  };

  // Get thumbnail images for the active rule preview
  const getPreviewThumbnails = () => {
    if (!activeRulePreview || !report?.thumbnails) return [];
    const pages = activeRulePreview.evidence?.pages || [];
    return pages.map((pageIdx, idx) => {
      const thumbnail = report.thumbnails[pageIdx];
      if (thumbnail && thumbnail.imageData) {
        return {
          index: idx,
          pageIdx: pageIdx,
          page: thumbnail.page || (pageIdx + 1),
          imageData: thumbnail.imageData
        };
      }
      return null;
    }).filter(Boolean);
  };

  // Get the currently active preview image
  const getActivePreviewImage = () => {
    const thumbnails = getPreviewThumbnails();
    if (thumbnails.length === 0 || activePreviewIndex >= thumbnails.length) return null;
    return thumbnails[activePreviewIndex];
  };

  const openImagePreview = (src, alt) => {
    setImagePreview({ open: true, src, alt });
  };

  const closeImagePreview = () => {
    setImagePreview({ open: false, src: '', alt: '' });
  };

  // Handle sending chat message
  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading || !markdownFiles || markdownFiles.length === 0) {
      return;
    }

    const question = chatInput.trim();
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date()
    };

    // Add user message
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      // Get markdown content from first file
      const markdownContent = markdownFiles[0]?.markdownContent || '';
      
      if (!markdownContent) {
        throw new Error('لا يوجد محتوى المستند');
      }

      // Get imageList and segments from markdownFiles
      const imageList = markdownFiles[0]?.imageList || [];
      const segments = markdownFiles[0]?.segments || [];

      // Build conversation history for context
      const conversationHistory = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Call chat API with evidence data
      const result = await api.chatWithDocument(question, markdownContent, conversationHistory, imageList, segments);

      if (!result.success) {
        throw new Error(result.error || t('step3.chat.error'));
      }

      // Add AI response
      const aiMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: result.answer,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, aiMessage]);

      // Auto-scroll to bottom
      setTimeout(() => {
        const container = document.getElementById('chat-messages-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);

    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage = err.response?.data?.error || err.message || t('step3.chat.error');
      
      // Add error message
      const errorMsg = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `${t('step3.chat.error')}: ${errorMessage}`,
        timestamp: new Date(),
        isError: true
      };
      
      setChatMessages(prev => [...prev, errorMsg]);
      showToast(errorMessage, 'error');
    } finally {
      setChatLoading(false);
    }
  };

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (activeTab === 'chat') {
      const container = document.getElementById('chat-messages-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [chatMessages, activeTab]);

  // Close image preview on ESC key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && imagePreview.open) {
        closeImagePreview();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [imagePreview.open]);

  // Show loading screen while processing
  if (loading) {
    return (
      <div className="step-container">
        <div className="loading-container">
          <div className="loader-con">
            <div style={{ '--i': 0 }} className="pfile"></div>
            <div style={{ '--i': 1 }} className="pfile"></div>
            <div className="pfile" style={{ '--i': 2 }}></div>
            <div className="pfile" style={{ '--i': 3 }}></div>
            <div className="pfile" style={{ '--i': 4 }}></div>
            <div className="pfile" style={{ '--i': 5 }}></div>
          </div>
          <p className="loading-text">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="step-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="btn-restart-error" onClick={onReset}>
            {t('step3.startOver')}
          </button>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <div className="step-container">
      {/* Step Header with Action Buttons */}
      <div className="step-header-with-actions">
        <div className="step-header-content">
          <h2>{t('step3.title')}</h2>
          <p className="step-description">{t('step3.description')}</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handlePrintReport}>
            <span>🖨️</span>
            <span>{t('step3.printReport')}</span>
          </button>
          <button className="btn-danger" onClick={onReset}>{t('step3.newValidation')}</button>
        </div>
      </div>

      {/* Form Card - Same style as Step 1 & 2 */}
      <div className="form-card-report" ref={reportRef}>
        {/* Timing Stats */}
        <div className="timing-stats">
          <div className="timing-item">
            <span className="timing-label">{t('step3.datalabTime')}</span>
            <span className="timing-value">{formatTime(datalabTime)}</span>
          </div>
          <div className="timing-item">
            <span className="timing-label">{t('step3.aiTime')}</span>
            <span className="timing-value">{formatTime(aiTime)}</span>
          </div>
          <div className="timing-item total">
            <span className="timing-label">{t('step3.totalTime')}</span>
            <span className="timing-value">{formatTime(totalTime)}</span>
          </div>
        </div>

        {/* Final Status Banner */}
        {report.finalStatus && (
          <div className={`final-status-banner ${report.finalStatus}`}>
            <div className="final-status-icon">
              {report.finalStatus === 'success' && '✓'}
              {report.finalStatus === 'partial' && '⚠'}
              {report.finalStatus === 'failed' && '✗'}
              {report.finalStatus === 'needs_review' && '👁'}
            </div>
            <div className="final-status-content">
              <h3 className="final-status-title">
                {report.finalStatus === 'success' && t('status.success')}
                {report.finalStatus === 'partial' && t('status.partial')}
                {report.finalStatus === 'failed' && t('status.failedReview')}
                {report.finalStatus === 'needs_review' && t('status.needsAdditionalReview')}
              </h3>
              {report.summary && (
                <p className="final-status-summary">
                  {lang === 'ar' ? (report.summary.ar || report.summary.en || '') : (report.summary.en || report.summary.ar || '')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="tabs-container">
          <div className="tabs-nav">
            <button 
              className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              📋 {t('step3.tab.report')}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'segments' ? 'active' : ''}`}
              onClick={() => setActiveTab('segments')}
            >
              📄 {t('step3.tab.segments')}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              💬 {t('step3.tab.chat')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="tabs-content">
            {/* TAB 1: REPORT (Validation Results) */}
            {activeTab === 'report' && (
              <>
              {/* Summary Stats */}
              <div className="summary-stats">
                  <div className="stat-card success">
                    <h3>{report.rulesIndex.filter(r => r.decision === 'passed' || r.decision === 'not_applicable').length}</h3>
                    <p>{t('step3.matchedRequirements')}</p>
                  </div>
                  <div className="stat-card failed">
                    <h3>{report.rulesIndex.filter(r => r.decision === 'failed' || r.decision === 'needs_review').length}</h3>
                    <p>{t('step3.unmatchedRequirements')}</p>
                  </div>
                  <div className="stat-card total">
                    <h3>{report.rulesIndex.length}</h3>
                    <p>{t('step3.totalRequirements')}</p>
                  </div>
                </div>
              <div className={`report-with-preview-layout ${activeRulePreview ? 'preview-active' : ''}`}>
                
                 
                {/* Document Preview Panel - Left Side */}
                {activeRulePreview && (
                  <div className="document-preview-panel">
                    {/* Panel Header */}
                    <div className="preview-panel-header">
                      <div className="preview-panel-title">
                        <span className="preview-icon">📄</span>
                        <span>{t('step3.previewEvidence')}</span>
                      </div>
                      <div className="preview-panel-actions">
                        <button 
                          className="preview-action-btn" 
                          onClick={() => {
                            const activeImg = getActivePreviewImage();
                            if (activeImg) openImagePreview(activeImg.imageData, `${t('step3.page')} ${activeImg.page}`);
                          }}
                          title={lang === 'ar' ? 'فتح في نافذة كاملة' : 'Open fullscreen'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                            <polyline points="15 3 21 3 21 9"/>
                            <line x1="10" y1="14" x2="21" y2="3"/>
                          </svg>
                        </button>
                        <button className="preview-close-btn" onClick={closeInlinePreview} title={t('common.close')}>
                          ✕
                        </button>
                      </div>
                    </div>

                    {/* Document Info */}
                    <div className="preview-document-info">
                      <span className="doc-icon">📁</span>
                      <span className="doc-name">{lang === 'ar' ? (activeRulePreview.title?.ar || activeRulePreview.title || t('step3.document')) : (activeRulePreview.title?.en || activeRulePreview.title || t('step3.document'))}</span>
                    </div>

                    {/* Main Preview Area */}
                    <div className="preview-main-area">
                      {(() => {
                        const activeImg = getActivePreviewImage();
                        if (activeImg) {
                          return (
                            <img 
                              src={activeImg.imageData} 
                              alt={`${t('step3.page')} ${activeImg.page}`}
                              className="preview-main-image"
                              onClick={() => openImagePreview(activeImg.imageData, `${t('step3.page')} ${activeImg.page}`)}
                            />
                          );
                        }
                        return (
                          <div className="preview-placeholder">
                            <span>{t('step3.noPreviewImage')}</span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Thumbnails Strip */}
                    <div className="preview-thumbnails-strip">
                      <div className="thumbnails-info">
                        <span>{t('step3.document')}: {activePreviewIndex + 1} {t('step3.of')} {getPreviewThumbnails().length}</span>
                      </div>
                      <div className="thumbnails-row">
                        {getPreviewThumbnails().map((thumb, idx) => (
                          <div 
                            key={idx}
                            className={`preview-thumbnail ${activePreviewIndex === idx ? 'active' : ''}`}
                            onClick={() => setActivePreviewIndex(idx)}
                          >
                            <img src={thumb.imageData} alt={`${t('step3.page')} ${thumb.page}`} />
                            <span className="thumb-page-label">{lang === 'ar' ? 'ص' : 'p'} {thumb.page}</span>
                          </div>
                        ))}
                      </div>
                      <div className="thumbnails-hint">
                        {t('step3.thumbnailsHint')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Report Cards - Right Side */}
                <div className="tab-panel report-cards-layout">

              {report.rulesIndex.map((rule, index) => {
                const isPassed =
                  rule.decision === 'passed' || rule.decision === 'not_applicable';
            
                const confidence =
                  rule.confidence !== undefined
                    ? Math.round(rule.confidence * 100)
                    : null;

                const isActivePreview = activeRulePreview?.ruleId === rule.ruleId;

                // Get title based on language - use AR for Arabic, EN for English
                const ruleTitle = lang === 'ar' 
                  ? (rule.title?.ar || rule.title?.en || rule.title) 
                  : (rule.title?.en || rule.title?.ar || rule.title);
            
                return (
                  <div
                    key={index}
                    className={`document-card ${isPassed ? 'passed' : 'failed'} ${isActivePreview ? 'preview-active-card' : ''}`}
                  >
                    {/* Header */}
                    <div className="document-card-header">

                      <div className="doc-title">  
                      <span className="rule-number">{index + 1}</span>                     
                        <h3>{ruleTitle}</h3>
                      </div>
            
                        <div className="doc-actions">
                          <div className={`status-badge ${isPassed ? 'success' : 'error'}`}>
                           {isPassed ? <><BadgeCheckIcon size={14} /> {t('status.passed')}</> : <><XCircleIcon size={14} color="red" /> {t('status.failed')}</>}
                          </div>

                        {rule.evidence?.pages?.length > 0 && (
                          <button
                            className="preview-btn"
                            onClick={() => handlePreview(rule)}
                          >
                            <EyeIcon size={14} color="blue" /> {t('step3.preview')}
                          </button>
                        )}
                      </div>
                    </div>
            
                    {/* Result Message */}
                    <div className="document-card-result">
                      <span className="result-icon">
                        {isPassed ? <CheckCircleIcon size={18} color="#12b76a" /> : <CircleAlertIcon size={18} color="#f04438" />}
                      </span>
                      <p>{lang === 'ar' ? (rule.message?.ar || rule.message?.en) : (rule.message?.en || rule.message?.ar)}</p>
                    </div>
            
                    <div className="document-card-body">
                      {/* Matched Fields */}
                      <div className="matched-fields">
                        <h4>{t('step3.matchedFields')}</h4>
            
                        {(rule.matchedFields || []).map((field, i) => (
                          <div key={i} className={`matched-field ${field.match ? 'match-success' : 'match-fail'}`}>
                            <div className="field-key">{field.key}</div>
                            <div className="field-values">
                              <div className="field-row">
                                <span className="field-label">{t('step3.expected')}</span>
                                <span className="expected">{field.expected || '—'}</span>
                              </div>
                              <div className="field-row">
                                <span className="field-label">{t('step3.found')}</span>
                                <span className={`found ${field.match ? '' : 'mismatch'}`}>{field.found || '—'}</span>
                              </div>
                            </div>
                            <span className={`match-status ${field.match ? 'success' : 'error'}`}>
                              {field.match ? <><CheckCircleIcon size={14} /> {t('step3.matched')}</> : <><XCircleIcon size={14} /> {t('step3.notMatched')}</>}
                            </span>
                          </div>
                        ))}
            
                        {(!rule.matchedFields || rule.matchedFields.length === 0) && (
                          <div className="matched-field empty">{t('step3.noFieldsToCompare')}</div>
                        )}
                      </div>
            
                      {/* AI Snippet */}
                      <div className="ai-snippet">
                        <div className="snippet-header">
                          <span>{t('step3.aiSnippet')}</span>
                          {confidence !== null && (
                            <span className="confidence-chip">{t('step3.confidence')} {confidence}%</span>
                          )}
                        </div>
            
                        <blockquote>
                          {lang === 'ar' ? (rule.evidence?.quote?.ar || rule.evidence?.quote?.exact || '—') : (rule.evidence?.quote?.en || rule.evidence?.quote?.exact || '—')}
                        </blockquote>
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
           
              <div className="tab-panel">
                {/* Display Mode Toggle */}
                {/* <div className="report-controls">
                  <div className="display-mode-toggle">
                    <button 
                      className={`toggle-btn ${displayMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setDisplayMode('grid')}
                      title="عرض شبكي"
                    >
                      ⊞ شبكي
                    </button>
                    <button 
                      className={`toggle-btn ${displayMode === 'table' ? 'active' : ''}`}
                      onClick={() => setDisplayMode('table')}
                      title="عرض جدولي"
                    >
                      ☰ جدولي
                    </button>
                  </div>
                </div> */}

               

                {/* Rules Display */}
                {/* {displayMode === 'grid' ? (
                  <div className="rules-grid">
                    {report.rulesIndex.map((rule, index) => {
                      const decisionClass = getDecisionClass(rule.decision);
                      const decisionIcon = getDecisionIcon(rule.decision);
                      
                      return (
                        <div key={index} className={`rule-card ${decisionClass}`}>
                          <div className="rule-header">
                            <span className="rule-number">{index + 1}</span>
                            <h3 className="rule-name">{rule.title?.ar || rule.title}</h3>
                            <span className={`status-icon ${decisionClass}`}>
                              {decisionIcon}
                            </span>
                          </div>
                          <div className="rule-body">
                            <p className="rule-info">{rule.message?.ar || rule.message?.en || ''}</p>
                            {rule.confidence !== undefined && (
                              <div className="rule-confidence">
                                <small>الثقة: {Math.round(rule.confidence * 100)}%</small>
                              </div>
                            )}
                            {rule.evidence?.pages && rule.evidence.pages.length > 0 && (
                              <button 
                                className="btn-preview"
                                onClick={() => handlePreview(rule)}
                              >
                                معاينة الأدلة ({rule.evidence.pages.length})
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rules-table-container">
                    <table className="rules-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>اسم الشرط</th>
                          <th>الحالة</th>
                          <th>الثقة</th>
                          <th>الملاحظات</th>
                          <th>الإجراء</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.rulesIndex.map((rule, index) => {
                          const decisionClass = getDecisionClass(rule.decision);
                          const decisionLabel = getDecisionLabel(rule.decision);
                          const decisionIcon = getDecisionIcon(rule.decision);
                          
                          return (
                            <tr key={index} className={decisionClass}>
                              <td>{index + 1}</td>
                              <td>{rule.title?.ar || rule.title}</td>
                              <td>
                                <span className={`status-badge ${decisionClass}`}>
                                  {decisionIcon} {decisionLabel}
                                </span>
                              </td>
                              <td>
                                {rule.confidence !== undefined ? (
                                  <span className="confidence-badge">
                                    {Math.round(rule.confidence * 100)}%
                                  </span>
                                ) : (
                                  <span>—</span>
                                )}
                              </td>
                              <td>{rule.message?.ar || rule.message?.en || ''}</td>
                              <td>
                                {rule.evidence?.pages && rule.evidence.pages.length > 0 ? (
                                  <button 
                                    className="btn-preview-small"
                                    onClick={() => handlePreview(rule)}
                                  >
                                    معاينة
                                  </button>
                                ) : (
                                  <span className="no-evidence">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )} */}
              </div>
              </>
            )}

            {/* TAB 2: SEGMENTS (Document Sections with Thumbnails) */}
            {activeTab === 'segments' && (
              <div className="tab-panel">
                {report.segments && report.segments.length > 0 ? (
                  <div className="segments-container">
                    <div className="segments-header">
                      <h3>{t('segments.title')}</h3>
                      <p className="segments-info">
                        {t('segments.count', { count: report.segments.length })} • 
                        {t('segments.imagesAvailable', { count: report.thumbnails?.length || 0 })}
                      </p>
                    </div>

                    <div className="segments-list">
                      {report.segments.map((segment, idx) => {
                        // Use title from segment or fallback
                        const originalTitle = segment.title || segment.name || (lang === 'ar' ? `قسم ${idx + 1}` : `Section ${idx + 1}`);
                        const segmentTitle = translateSegmentTitle(originalTitle);
                        
                        // Get page range from segment
                        const pages = segment.pages || [];
                        const fromPage = segment.from_page || (pages.length > 0 ? Math.min(...pages) + 1 : 1);
                        const toPage = segment.to_page || (pages.length > 0 ? Math.max(...pages) + 1 : 1);
                        
                        // Get page thumbnails from report.thumbnails array
                        let pageThumbnails = [];
                        if (report.thumbnails && Array.isArray(report.thumbnails) && pages.length > 0) {
                          pageThumbnails = pages
                            .map(pageIdx => {
                              const thumbnail = report.thumbnails[pageIdx];
                              if (thumbnail && thumbnail.imageData) {
                                return {
                                  page: pageIdx + 1,
                                  imageData: thumbnail.imageData
                                };
                              }
                              return null;
                            })
                            .filter(Boolean);
                        }

                        return (
                          <div key={idx} className="segment-card-simple">
                            <div className="segment-header-simple">
                              <div className="segment-title-group">
                                <span className="rule-number">{idx + 1}</span> 
                                <h4><span className="rule-title">{segmentTitle}</span></h4>
                              </div>
                              <span className="segment-pages-badge">
                                {pages.length === 1 
                                  ? `${t('step3.page')} ${fromPage}`
                                  : `${t('step3.pages')} ${fromPage} - ${toPage}`
                                }
                              </span>
                            </div>
                            
                            {/* Page Thumbnails Section */}
                            {pageThumbnails.length > 0 ? (
                              <div className="segment-thumbnails">
                                {pageThumbnails.map((thumb, thumbIdx) => (
                                    <div key={thumbIdx} className="thumbnail-item">
                                      <img 
                                        src={thumb.imageData} 
                                        alt={`${t('step3.page')} ${thumb.page}`}
                                        onClick={() => openImagePreview(thumb.imageData, `${t('step3.page')} ${thumb.page}`)}
                                        style={{ cursor: 'pointer' }}
                                        onError={(e) => {
                                          console.error('❌ Image failed to load:', thumb.page);
                                          e.target.style.border = '2px solid red';
                                        }}
                                      />
                                      <span className="thumbnail-label">{t('step3.page')} {thumb.page}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-thumbnails">
                                <p>{t('segments.noThumbnails')}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="no-segments">
                    <p>{t('segments.noData')}</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: CHAT (Chat with Document) */}
            {activeTab === 'chat' && (
              <div className="tab-panel chat-tab-panel">
                <div className="chat-container">
                  {/* Chat Header */}
                  <div className="chat-header">
                    <h3>{t('step3.chat.title')}</h3>
                    {chatMessages.length > 0 && (
                      <button 
                        className="btn-clear-chat"
                        onClick={() => {
                          setChatMessages([]);
                          setChatInput('');
                        }}
                      >
                        {t('step3.chat.clear')}
                      </button>
                    )}
                  </div>

                  {/* Messages Container */}
                  <div className="chat-messages" id="chat-messages-container">
                    {chatMessages.length === 0 ? (
                      <div className="chat-empty-state">
                        <div className="chat-empty-icon">💬</div>
                        <p>{t('step3.chat.empty')}</p>
                      </div>
                    ) : (
                      chatMessages.map((message) => (
                        <div 
                          key={message.id} 
                          className={`chat-message ${message.role === 'user' ? 'user-message' : 'ai-message'}`}
                        >
                          <div className="message-content">
                            <div className="message-text">{message.content}</div>
                            <div className="message-actions">
                              {message.role === 'assistant' && (
                                <button
                                  className="message-copy-btn"
                                  onClick={() => {
                                    navigator.clipboard.writeText(message.content);
                                    showToast(t('step3.chat.copied'), 'success');
                                  }}
                                  title={t('step3.chat.copy')}
                                >
                                  📋
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="message-timestamp">
                            {new Date(message.timestamp).toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      ))
                    )}
                    {chatLoading && (
                      <div className="chat-message ai-message">
                        <div className="message-content">
                          <div className="message-text">
                            <div className="typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                            {t('step3.chat.loading')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input Area */}
                  <div className="chat-input-area">
                    <textarea
                      className="chat-input"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder={t('step3.chat.placeholder')}
                      disabled={chatLoading || !markdownFiles || markdownFiles.length === 0}
                      rows={1}
                      style={{
                        minHeight: '44px',
                        maxHeight: '120px',
                        resize: 'none',
                        overflowY: 'auto'
                      }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                    <button
                      className="chat-send-btn"
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim() || chatLoading || !markdownFiles || markdownFiles.length === 0}
                    >
                      {chatLoading ? '⏳' : '➤'}
                    </button>
                  </div>

                  {(!markdownFiles || markdownFiles.length === 0) && (
                    <div className="chat-warning">
                      {t('step3.chat.empty')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Preview Modal */}
      {previewModal.open && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{lang === 'ar' ? (previewModal.rule?.title?.ar || previewModal.rule?.title) : (previewModal.rule?.title?.en || previewModal.rule?.title)}</h3>
              <button className="btn-close" onClick={closePreview}>✕</button>
            </div>
            <div className="preview-modal-content">
              {/* Decision and Confidence */}
              <div className="preview-meta">
                <span className={`status-badge ${getDecisionClass(previewModal.rule?.decision)}`}>
                  {getDecisionIcon(previewModal.rule?.decision)} {getDecisionLabel(previewModal.rule?.decision)}
                </span>
                {previewModal.rule?.confidence !== undefined && (
                  <span className="confidence-badge">
                    {t('step3.confidenceLabel')} {Math.round(previewModal.rule.confidence * 100)}%
                  </span>
                )}
              </div>

              {/* Message */}
              {previewModal.rule?.message && (
                <div className="preview-explanation">
                  {lang === 'ar' ? (previewModal.rule.message.ar || previewModal.rule.message.en || '') : (previewModal.rule.message.en || previewModal.rule.message.ar || '')}
                </div>
              )}

              {/* Quote Evidence */}
              {previewModal.rule?.evidence?.quote && (
                <div className="preview-quote">
                  <strong>{lang === 'ar' ? 'الاقتباس من المستند:' : 'Document Quote:'}</strong>
                  <blockquote>{lang === 'ar' ? (previewModal.rule.evidence.quote.ar || previewModal.rule.evidence.quote.exact) : (previewModal.rule.evidence.quote.en || previewModal.rule.evidence.quote.exact)}</blockquote>
                </div>
              )}
              
              {/* Images - Load from report.thumbnails using page indices */}
              {previewModal.pages && previewModal.pages.length > 0 && report.thumbnails ? (
                <div className="preview-images-grid">
                  {previewModal.pages.map((pageIdx, idx) => {
                    // pageIdx is 0-based, so thumbnails[pageIdx] gives us the correct thumbnail
                    const thumbnail = report.thumbnails[pageIdx];
                    if (!thumbnail || !thumbnail.imageData) {
                      console.warn(`No thumbnail found at index ${pageIdx}`);
                      return null;
                    }
                    const displayPage = thumbnail.page || (pageIdx + 1);
                    return (
                      <div key={idx} className="preview-image-card" onClick={() => openImagePreview(thumbnail.imageData, `${lang === 'ar' ? 'دليل' : 'Evidence'} ${idx + 1} - ${t('step3.page')} ${displayPage}`)}>
                        <img src={thumbnail.imageData} alt={`${lang === 'ar' ? 'دليل' : 'Evidence'} ${idx + 1}`} style={{ cursor: 'pointer' }} />
                        <div className="preview-image-info">
                          <span>{t('step3.page')} {displayPage}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="preview-no-images">
                  <p>{t('step3.noEvidence')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Image Preview Modal */}
      {imagePreview.open && (
        <div className="fullscreen-image-modal" onClick={closeImagePreview}>
          <div className="fullscreen-image-container">
            <button className="fullscreen-close-btn" onClick={closeImagePreview}>
              <span>×</span>
            </button>
            <img 
              src={imagePreview.src} 
              alt={imagePreview.alt}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="fullscreen-image-label">{imagePreview.alt}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Step3Report;
